import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import LoadingSpinner from './components/LoadingSpinner';
import Comments from './components/Comments';
import { toast } from 'react-hot-toast';
import { formatTypeLabel } from './utils/formatTypeLabel';
import { fetchWithCSRF } from './utils/csrf';
import { getFirstTouchQueryString } from './utils/attribution';
import './OpportunityDetail.css';

const OpportunityDetail = () => {
  const { hash } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loginWithPopup } = useAuth0();
  
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const commentsRef = useRef(null);
  
  // Check if we should show comments from location state
  const shouldShowComments = location.state?.showComments || false;
  const shouldShowForm = location.state?.showForm || false;

  // Scroll to comments if shouldShowComments is true
  useEffect(() => {
    if (shouldShowComments && commentsRef.current && opportunity) {
      // Small delay to ensure the comments component has rendered
      const timer = setTimeout(() => {
        commentsRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [shouldShowComments, opportunity]);

  const handleEmailSignup = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      // Save email to database with status "new" (no auth required)
      const ensureBody = { 
          email: email,
          name: null // We don't have the name yet
        };
      const firstTouchQs = getFirstTouchQueryString();
      if (firstTouchQs) ensureBody.query_string = firstTouchQs;
      const response = await fetchWithCSRF(`${process.env.REACT_APP_API_URL || '/api'}/members/ensure`, {
        method: 'POST',
        body: JSON.stringify(ensureBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 409) {
          // Email already exists - continue to signup anyway
        } else {
          console.error('Database save failed:', errorData);
          // Continue to Auth0 signup even if database save fails
        }
      }

      // Open Auth0 signup with pre-filled email
      await loginWithPopup({ 
        authorizationParams: { 
          screen_hint: 'signup',
          login_hint: email
        } 
      });
    } catch (error) {
      if (error.error === 'cancelled' || error.message === 'Popup closed') {
        // User cancelled, no need to show error
      } else {
        console.error('Signup error:', error);
        toast.error('There was an error with signup. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const baseUrl = `${process.env.REACT_APP_API_URL || '/api'}/opportunities/${hash}`;

  const fetchOpportunity = useCallback(async (isRetry = false, retryFor304 = 0, fetchUrl = baseUrl) => {
    try {
      if (!isRetry) {
        setLoading(true);
        setOpportunity(null);
        setRetryCount(0);
      }

      const response = await fetch(fetchUrl, { cache: 'no-store' });

      if (!response.ok) {
        if (response.status === 404) {
          toast.error('Opportunity not found');
          setLoading(false);
          return;
        } else if (response.status === 500 && retryCount < 2) {
          // Retry on server errors
          setRetryCount(prev => prev + 1);
          setTimeout(() => fetchOpportunity(true, 0, baseUrl), 1000);
          return;
        } else if (response.status === 500) {
          toast.error('Server error - please try again');
          setLoading(false);
          return;
        } else {
          toast.error('Failed to load opportunity');
          setLoading(false);
          return;
        }
      }

      // 304 has empty body - response.json() would throw. Retry with cache-bust to get full body
      if (response.status === 304 && retryFor304 < 2) {
        const cacheBustUrl = `${baseUrl}?_=${Date.now()}`;
        return fetchOpportunity(isRetry, retryFor304 + 1, cacheBustUrl);
      }

      let data;
      try {
        data = await response.json();
      } catch (parseErr) {
        throw parseErr;
      }
      setOpportunity(data);
      setLoading(false);
    } catch (err) {
      if (retryCount < 2) {
        // Retry on network errors
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchOpportunity(true), 1000);
      } else {
        toast.error('Network error - please check your connection and try again');
        setLoading(false);
      }
    }
  }, [hash, retryCount]);

  useEffect(() => {
    fetchOpportunity();
  }, [hash, fetchOpportunity]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Date not available';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatRate = (opportunity) => {
    if (opportunity.rate) return opportunity.rate;
    if (opportunity.calculated_rate) return `$${opportunity.calculated_rate}`;
    if (opportunity.word_rate) return `$${opportunity.word_rate}/word`;
    if (opportunity.piece_rate) return `$${opportunity.piece_rate}/piece`;
    return 'Rate not specified';
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  const getCategoryColor = (category) => {
    const colorMap = {
      'Arts and Culture': '#3864B4',
      'Creative': '#b438a2',
      'Content': '#b48838',
      'News': '#38B44a'
    };
    return colorMap[category] || '#666';
  };

  const getOpportunityCategories = (opportunity) => {
    if (!opportunity.areas) return [];
    
    const areas = opportunity.areas.split(',').map(a => a.trim());
    const categories = [];
    
    // Map areas directly to categories (areas field contains category names)
    areas.forEach(area => {
      const normalized = area.toLowerCase();
      if (normalized.includes('arts') || normalized.includes('culture')) {
        if (!categories.includes('Arts and Culture')) categories.push('Arts and Culture');
      } else if (normalized.includes('creative')) {
        if (!categories.includes('Creative')) categories.push('Creative');
      } else if (normalized.includes('content')) {
        if (!categories.includes('Content')) categories.push('Content');
      } else if (normalized.includes('news')) {
        if (!categories.includes('News')) categories.push('News');
      }
    });
    
    return categories;
  };

  const getCategoryGradient = (categories) => {
    if (!categories || categories.length === 0) return '#e0e0e0';
    if (categories.length === 1) return getCategoryColor(categories[0]);
    
    // Create gradient with equal sections for each category
    const percentage = 100 / categories.length;
    const gradientStops = categories.map((cat, index) => {
      const start = index * percentage;
      const end = (index + 1) * percentage;
      return `${getCategoryColor(cat)} ${start}%, ${getCategoryColor(cat)} ${end}%`;
    }).join(', ');
    
    return `linear-gradient(90deg, ${gradientStops})`;
  };


  if (loading) {
    return (
      <div style={{ padding: '2rem', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <LoadingSpinner message="Loading opportunity..." />
      </div>
    );
  }

  // Remove error display - just show loading spinner and let React Query retry

  if (!opportunity) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>Opportunity not found</h1>
        <button 
          onClick={() => navigate('/opportunities')}
          style={{
            backgroundColor: '#38b44a',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            fontSize: '16px',
            cursor: 'pointer',
            marginTop: '1rem'
          }}
        >
          Back to Opportunities
        </button>
      </div>
    );
  }

  const categories = getOpportunityCategories(opportunity);

  // Determine response button behavior
  const getResponseButton = () => {
    if (opportunity.reply_to && opportunity.reply_to.trim()) {
      // Use mailto link if reply_to exists (takes precedence)
      return {
        href: `mailto:${opportunity.reply_to.trim()}`,
        isMailto: true
      };
    } else if (opportunity.pitch_link_url && opportunity.pitch_link_url.trim()) {
      // Use pitch_link_url if reply_to is empty
      return {
        href: opportunity.pitch_link_url.trim(),
        isMailto: false
      };
    } else if (opportunity.pitch_link && opportunity.pitch_link.trim()) {
      // Fallback to pitch_link if pitch_link_url doesn't exist
      return {
        href: opportunity.pitch_link.trim(),
        isMailto: false
      };
    }
    return null;
  };

  const responseButton = getResponseButton();

  // Response button component - matches "View Details" styling from Opportunities page
  const ResponseButton = ({ style = {} }) => {
    if (!responseButton) return null;

    const buttonStyle = {
      backgroundColor: '#38b44a',
      color: 'white',
      border: 'none',
      padding: '0.5rem 1rem',
      borderRadius: '4px',
      fontSize: '0.875rem',
      fontWeight: '500',
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-block',
      transition: 'background-color 0.2s',
      ...style
    };

    const handleMouseOver = (e) => {
      e.target.style.backgroundColor = '#2d8f3a';
    };

    const handleMouseOut = (e) => {
      e.target.style.backgroundColor = '#38b44a';
    };

    if (responseButton.isMailto) {
      return (
        <a 
          href={responseButton.href} 
          style={buttonStyle}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          Respond to Opportunity
        </a>
      );
    } else {
      return (
        <a 
          href={responseButton.href}
          target="_blank"
          rel="noopener noreferrer"
          style={buttonStyle}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          Respond to Opportunity
        </a>
      );
    }
  };

  return (
    <>
      <style>
        {`
          .full-width-break {
            display: inline;
          }
          @media (min-width: 768px) {
            .full-width-break {
              display: block;
            }
          }
        `}
      </style>
      <div style={{ 
        padding: '2rem', 
        paddingTop: '2rem', 
        maxWidth: '800px', 
        margin: '0 auto',
        minHeight: '100vh'
      }}>
      {/* Back Button - Only show for authenticated users */}
      {isAuthenticated && (
        <button 
          onClick={() => navigate(-1)}
          style={{
            backgroundColor: 'transparent',
            color: '#38b44a',
            border: '2px solid #38b44a',
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            cursor: 'pointer',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          ← Back
        </button>
      )}

      {/* Call to Action for Unauthorized Users */}
      {!isAuthenticated && (
        <div style={{
          backgroundColor: '#f8f9fa',
          border: '2px solid #38b44a',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#333',
            margin: '0 0 1rem 0'
          }}>
            Want to see more opportunities like this?
          </h2>
          <p style={{
            fontSize: '1rem',
            color: '#666',
            marginBottom: '1.5rem',
            lineHeight: '1.5'
          }}>
            Join Study Hall to access more opportunities like this one, search our editor database,
            <span style={{ display: 'block' }} className="full-width-break">
              connect with other writers and journalists, and grow your career.
            </span>
          </p>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            maxWidth: '500px',
            margin: '0 auto',
            position: 'relative'
          }}>
            <input
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 140px 12px 16px',
                border: '2px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '16px',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#38b44a'}
              onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
            />
            <button 
              onClick={handleEmailSignup}
              disabled={isSubmitting}
              style={{
                position: 'absolute',
                right: '4px',
                top: '4px',
                bottom: '4px',
                backgroundColor: isSubmitting ? '#ccc' : '#38b44a',
                color: 'white',
                border: 'none',
                padding: '0 16px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
                whiteSpace: 'nowrap'
              }}
              onMouseOver={(e) => {
                if (!isSubmitting) {
                  e.target.style.backgroundColor = '#2d8f3a';
                }
              }}
              onMouseOut={(e) => {
                if (!isSubmitting) {
                  e.target.style.backgroundColor = '#38b44a';
                }
              }}
            >
              {isSubmitting ? 'Signing Up...' : 'Sign Up'}
            </button>
          </div>
        </div>
      )}

      {/* Opportunity Card */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0',
        overflow: 'hidden'
      }}>
        {/* Colored header */}
        <div style={{
          height: '8px',
          background: getCategoryGradient(categories)
        }}></div>

        <div style={{ padding: '2rem' }}>
          {/* Header */}
          <div style={{ marginBottom: '2rem' }}>
            <div className="opportunity-detail-header" style={{ marginBottom: '1rem' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 style={{ 
                  fontSize: '1.75rem', 
                  fontWeight: '700', 
                  color: '#333', 
                  margin: '0 0 0.5rem 0',
                  lineHeight: '1.3'
                }}>
                  {opportunity.title}
                </h1>
                <p style={{ 
                  fontSize: '1.125rem', 
                  color: '#666', 
                  margin: '0 0 0.5rem 0',
                  fontWeight: '500'
                }}>
                  {opportunity.brand_name}
                </p>
                <div style={{ 
                  fontSize: '1rem', 
                  fontWeight: '600', 
                  color: '#38b44a',
                  marginBottom: '0.75rem'
                }}>
                  {formatRate(opportunity)}
              </div>
              <div style={{ 
                fontSize: '0.875rem', 
                color: '#666', 
                  marginBottom: '1rem'
              }}>
                  Posted {formatDate(opportunity.created)}
                {opportunity.deadline && (
                  <div style={{ color: '#e74c3c', fontWeight: '500', marginTop: '0.25rem' }}>
                    Deadline: {formatDate(opportunity.deadline)}
                  </div>
                )}
                </div>
                {/* Response Button - Top */}
                <div style={{ marginTop: '1rem' }}>
                  <ResponseButton />
                </div>
              </div>
            </div>

            {/* Type badges */}
            {opportunity.type && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                {opportunity.type.split(',').map((type, index) => (
                  <span key={index} style={{
                    backgroundColor: '#f0f0f0',
                    color: '#666',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}>
                    {formatTypeLabel(type.trim())}
                  </span>
                ))}
              </div>
            )}

            {/* Topic badges */}
            {opportunity.topic && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {opportunity.topic.split(',').map((topic, index) => (
                  <span key={index} style={{
                    backgroundColor: 'white',
                    color: '#333',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    fontWeight: '500'
                  }}>
                    {topic.trim()}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          {opportunity.details && (
            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ 
                fontSize: '1.375rem', 
                fontWeight: '600', 
                color: '#333', 
                marginBottom: '1rem' 
              }}>
                Description
              </h2>
              <div 
                style={{ 
                  color: '#333', 
                  lineHeight: '1.7', 
                  fontSize: '1rem'
                }}
                dangerouslySetInnerHTML={{ __html: opportunity.details }}
              />
            </div>
          )}

          {/* Requirements */}
          {opportunity.requirements && (
            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ 
                fontSize: '1.375rem', 
                fontWeight: '600', 
                color: '#333', 
                marginBottom: '1rem' 
              }}>
                Requirements
              </h2>
              <div 
                style={{ 
                  color: '#333', 
                  lineHeight: '1.7', 
                  fontSize: '1rem'
                }}
                dangerouslySetInnerHTML={{ __html: opportunity.requirements }}
              />
            </div>
          )}

          {/* Contact Information */}
          {opportunity.contact_info && (
            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ 
                fontSize: '1.375rem', 
                fontWeight: '600', 
                color: '#333', 
                marginBottom: '1rem' 
              }}>
                Contact Information
              </h2>
              <div 
                style={{ 
                  color: '#333', 
                  lineHeight: '1.7', 
                  fontSize: '1rem'
                }}
                dangerouslySetInnerHTML={{ __html: opportunity.contact_info }}
              />
            </div>
          )}

          {/* Application Link */}
          {opportunity.application_url && (
            <div style={{ marginBottom: '2rem' }}>
              <a 
                href={opportunity.application_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  backgroundColor: '#38b44a',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  display: 'inline-block',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#2d8f3a'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#38b44a'}
              >
                Apply Now →
              </a>
            </div>
          )}

          {/* Response Button - Bottom */}
          <div style={{ marginTop: '2rem', marginBottom: '2rem', textAlign: 'center' }}>
            <ResponseButton />
          </div>

          {/* Comments Section */}
          <div ref={commentsRef} style={{ marginTop: '2rem' }}>
            <Comments 
              contentId={opportunity.id} 
              contentType="opportunity"
              showCommentsByDefault={shouldShowComments}
              showFormByDefault={shouldShowForm}
            />
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default OpportunityDetail;
